import React, { Component, Fragment } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import { roles as ChannelRoles } from 'remote/channels/roles'
import { types as ChannelTypes } from 'remote/channels/types'

import Dropdown from 'components/base/Dropdown'
import {
  ArrowUpIcon,
  AddPermissionIcon,
  CheckMarkIcon,
} from 'components/Utils/Icons'
import { Button, Text } from 'components/base'
import { Input, Label } from 'components/Utils/Form'
import ChannelMembershipPicker from 'components/Channels/ChannelMembershipPicker'
import ChannelMembershipList from 'components/Channels/ChannelMembershipList'
import ChannelTypeSelector from 'components/Channels/ChannelTypeSelector'
import { POPULAR_NAMES, KEYCODES, CHANNEL_INPUT_MAX_LENGTH } from './utils'
import PopularNamesDropDown from './PopularNamesDropDown'

export default class CreateChannelForm extends Component {
  static propTypes = {
    channelCreate: PropTypes.func.isRequired,
    currentUserId: PropTypes.string.isRequired,
  }

  inputRef = React.createRef()

  state = {
    templateSelected: null,
    dropDownIndex: null,
    isDropDownOpen: false,
    creationProcessing: false,
    advancedSettingsExpanded: false,
    templateListExpanded: false,
    showMembershipPicker: false,
    usersMemberships: [],
    userGroupsMemberships: [],
    channelName: '',
    type: ChannelTypes.PUBLIC,
    defaultRole: ChannelRoles.WRITER,
  }

  render() {
    return (
      <Fragment>
        {this.isDropDownOpen() && (
          <div
            onClick={this.hideDropDown}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
        <form
          className="padding-vertical-huge padding-horizontal-medium background-shade-100"
          style={{ width: 600 }}
          onSubmit={this.onFormSubmit}
        >
          <div className="flexbox flexbox-column flexbox-space-between">
            <Text.Title className="flexbox-grow">
              {this.getModalTitle()}
            </Text.Title>
            <Text.SubtitleSmall className="margin-top-medium">
              Channels let you organize your work into deparments like Marketing
              or Customer support.
            </Text.SubtitleSmall>
          </div>
          {this.renderTemplateList()}
          {this.renderInput()}
          {this.renderAdvancedSettings()}
          <div className="flexbox flexbox-justify-start margin-top-largest">
            <Button.Secondary
              className="margin-right-normal"
              onClick={this.props.closeModal}
            >
              Cancel
            </Button.Secondary>
            <Button.Primary
              disabled={this.isNameEmpty() || this.state.creationProcessing}
            >
              Create channel
            </Button.Primary>
          </div>
        </form>
      </Fragment>
    )
  }

  renderTemplateList = () => {
    const { templateListExpanded } = this.state
    const { userAssistDisplay, channelTemplates } = this.props

    if (userAssistDisplay || templateListExpanded) {
      return (
        <Fragment>
          <Label id="new-template" className="margin-top-larger">
            Choose a template
          </Label>
          <div className="flexbox flexbox-wrap">
            {channelTemplates.map(({ title, icon, id }) => {
              const selected = this.state.templateSelected === id
              const statusIcon = selected ? CheckMarkIcon : icon
              return (
                <div
                  key={id}
                  onClick={() => this.onClickTemplate(id)}
                  style={{ width: '50%', boxSizing: 'border-box' }}
                  className="padding-bottom-small padding-right-small"
                >
                  <div
                    className={classNames(
                      'flexbox flexbox-align-center pointer background-shade-45-on-hover radius-normal padding-normal',
                      {
                        'background-shade-50': selected,
                      }
                    )}
                  >
                    <div
                      className={classNames(
                        'flexbox flexbox-centered radius-normal padding-small margin-right-normal',
                        selected
                          ? 'background-primary shade-100-important'
                          : 'primary'
                      )}
                      style={{
                        boxShadow:
                          '0px 2px 6px rgba(0, 0, 0, 0.08), 0px 0px 2px rgba(0, 0, 0, 0.04)',
                      }}
                    >
                      {React.createElement(statusIcon, {
                        size: 12,
                      })}
                    </div>
                    <div className="shade-05 font-weight-medium font-size-large">
                      {title}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </Fragment>
      )
    }
  }

  renderInput = () => {
    return (
      <Fragment>
        <div className="flexbox flexbox-align-center margin-top-large">
          <Label id="new-channel-name">Pick a channel name</Label>
          {!this.props.userAssistDisplay &&
            !this.state.templateListExpanded && (
              <div className="flexbox flexbox-grow flexbox-justify-end margin-bottom-normal">
                <div
                  className="pointer primary font-weight-medium"
                  onClick={() => this.setState({ templateListExpanded: true })}
                >
                  See templates
                </div>
              </div>
            )}
        </div>
        <Dropdown.Wrapper className="width-full">
          <Input
            autoFocus={!this.props.userAssistDisplay}
            maxLength={CHANNEL_INPUT_MAX_LENGTH}
            ref={this.inputRef}
            id="new-channel-name"
            onChange={this.setName}
            onFocus={this.onFocus}
            value={this.state.channelName}
            onKeyDown={this.onKeyDown}
            autoComplete="off"
            group="right"
            className="width-full"
            required
            placeholder="E.g. General, Sales, Marketing"
            tabIndex="-1"
          />
          {this.isDropDownOpen() && (
            <PopularNamesDropDown
              showHeader={this.isNameEmpty()}
              options={this.getFilteredPopularNames()}
              highlightedIndex={this.state.dropDownIndex}
              onClickItem={this.onClickDropDownEntry}
            />
          )}
        </Dropdown.Wrapper>
      </Fragment>
    )
  }

  renderAdvancedSettings = () => {
    if (this.props.isOwnerOnboardingActive) {
      return null
    }
    const existingMembershipSubjectIds = [
      this.props.currentUserId,
      ...this.state.usersMemberships.map(({ user }) => user.id),
      ...this.state.userGroupsMemberships.map(({ userGroup }) => userGroup.id),
    ]

    return (
      <Fragment>
        {this.state.advancedSettingsExpanded && (
          <Fragment>
            <div className="flexbox margin-top-larger">
              <div className="flexbox flexbox-column flexbox-grow">
                <Label
                  className="margin-vertical-smaller"
                  id="new-channel-privacy"
                >
                  Make it private
                </Label>
                <div className="shade-10 margin-top-smaller">
                  {this.state.type === ChannelTypes.PUBLIC
                    ? 'This channel is open. Anyone in your team can view and write in it.'
                    : 'This channel is private. You can set individual member and group access below.'}
                </div>
              </div>
              <div className="flexbox flexbox-align-center">
                <ChannelTypeSelector
                  value={this.state.type}
                  onChange={this.toggleChannelType}
                  incentiveId="ManageChannelsPermissions"
                />
              </div>
            </div>
            <Label id="new-channel-permissions" className="margin-top-larger">
              Set access
            </Label>
            <ChannelMembershipList
              canManage={true}
              defaultRole={this.state.defaultRole}
              showDefaultRole={this.state.type === ChannelTypes.PUBLIC}
              onDefaultRoleChange={defaultRole =>
                this.setState({ defaultRole })
              }
              usersMemberships={this.state.usersMemberships}
              updateUserRole={this.updateUserRole}
              userGroupsMemberships={this.state.userGroupsMemberships}
              updateUserGroupRole={this.updateUserGroupRole}
            />
            {!this.state.showMembershipPicker && (
              <Text.Action
                className="flexbox"
                onClick={() => this.setState({ showMembershipPicker: true })}
              >
                <AddPermissionIcon
                  size={16}
                  className="margin-horizontal-smaller"
                />
                <div className="margin-left-normal">Set more permissions</div>
              </Text.Action>
            )}
            {this.state.showMembershipPicker && (
              <div className="flexbox">
                <div className="flexbox-grow">
                  <ChannelMembershipPicker
                    classname="margin-bottom-large"
                    inputProps={{
                      id: 'new-memberships',
                      placeholder: 'Search for a member or group',
                    }}
                    blacklistedIds={existingMembershipSubjectIds}
                    onSelection={({ item, role }) => {
                      switch (item.type) {
                        case 'User':
                          this.addUserMembership({ user: item.user, role })
                          return
                        case 'UserGroup':
                          this.addUserGroupMembership({
                            userGroup: item.userGroup,
                            role,
                          })
                          return
                        default:
                          throw new Error(`Unknown type ${item.type}`)
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </Fragment>
        )}
        {!this.state.advancedSettingsExpanded ? (
          <div className="flexbox flexbox-align-center margin-top-large">
            <Text.SubtitleSmall>
              {this.state.type === ChannelTypes.PUBLIC
                ? 'Anyone in your team can view and write in this channel.'
                : 'This channel is private and can only be viewed by specific people in your team.'}
            </Text.SubtitleSmall>
            <Text.PrimaryAction
              className="margin-left-smaller font-size-large line-height-larger"
              onClick={() => this.setState({ advancedSettingsExpanded: true })}
            >
              Edit access
            </Text.PrimaryAction>
          </div>
        ) : (
          <div className="flexbox flexbox-align-center margin-top-large primary primary-dark-on-hover">
            <ArrowUpIcon size={12} />
            <Text.PrimaryAction
              className="padding-left-smaller"
              onClick={() => {
                this.setState({
                  advancedSettingsExpanded: false,
                  showMembershipPicker: false,
                })
              }}
            >
              Hide advanced settings access
            </Text.PrimaryAction>
          </div>
        )}
      </Fragment>
    )
  }

  getModalTitle = () =>
    this.props.isOwnerOnboardingActive
      ? 'Create your first channel'
      : 'Create a channel'

  isNameEmpty = ({ channelName } = this.state) =>
    channelName.trim().length === 0
  hasPopularNamesMatch = () => this.getFilteredPopularNames().length > 0
  isDropDownOpen = () =>
    this.props.userAssistDisplay && this.state.isDropDownOpen

  getFilteredPopularNames = () =>
    POPULAR_NAMES.filter(popularName => {
      const formattedChannelName = this.state.channelName.replace(
        /[-[\]{}()*+?.,\\^$|#\s]/g,
        '\\$&'
      )
      return new RegExp(`^${formattedChannelName.toLowerCase()}`).test(
        popularName.toLowerCase()
      )
    }).sort((a, b) => a.localeCompare(b))

  onClickDropDownEntry = value =>
    this.setState({ channelName: value, isDropDownOpen: false }, () =>
      this.inputRef.current.focus()
    )

  onClickTemplate = templateId => {
    const { channelTemplates } = this.props
    const template = channelTemplates.find(({ id }) => id === templateId)
    const channelName = template.title !== 'Blank' ? template.title : ''

    this.setState({ templateSelected: templateId, channelName })
  }

  createChannel = () => {
    const {
      channelName,
      type,
      defaultRole,
      usersMemberships,
      userGroupsMemberships,
      templateSelected: publicShareToken,
      creationProcessing,
    } = this.state

    if (this.isNameEmpty() || creationProcessing) {
      return
    }

    this.setState({ creationProcessing: true }, () => {
      // Onboarding goes to next step
      if (this.props.isOwnerOnboardingActive) {
        this.props.updateUserPreferences({
          onboardingStep: 'done',
          isChannelOnboardingDone: true,
        })
      }

      // User decided to clone a channel
      if (publicShareToken) {
        this.props.cloneChannel({
          publicShareToken,
          type,
          name: this.state.channelName,
        })

        return
      }

      // User decided to create a channel
      this.props.channelCreate({
        input: {
          name: channelName,
          type,
          defaultRole,
          usersMemberships: usersMemberships.map(({ user, role }) => ({
            userId: user.id,
            role,
          })),
          userGroupsMemberships: userGroupsMemberships.map(
            ({ userGroup, role }) => ({ userGroupId: userGroup.id, role })
          ),
        },
      })
    })
  }

  onFocus = () => this.isNameEmpty() && this.showDropDown()

  onKeyDownOverflowUp = () => this.state.dropDownIndex <= 0
  onKeyDownOverflowDown = () => {
    const index =
      this.state.dropDownIndex === null ? 0 : this.state.dropDownIndex + 1
    return index >= this.getFilteredPopularNames().length
  }

  onKeyDown = event => {
    event.stopPropagation()
    if (event.keyCode === KEYCODES.ENTER) {
      event.preventDefault()
      if (this.isDropDownOpen() && this.state.dropDownIndex !== null) {
        const popularNames = this.getFilteredPopularNames()
        const selectedPopularName = popularNames[this.state.dropDownIndex]
        this.onClickDropDownEntry(selectedPopularName)
      } else {
        this.createChannel()
      }
    }
    if (event.keyCode === KEYCODES.UP) {
      if (this.onKeyDownOverflowUp()) {
        return
      }
      this.onMoveUp()
    }
    if (event.keyCode === KEYCODES.DOWN) {
      if (this.onKeyDownOverflowDown()) {
        return
      }
      this.onMoveDown()
    }
    if (event.keyCode === KEYCODES.ESC) {
      if (this.isDropDownOpen()) {
        this.hideDropDown()
      } else {
        this.props.closeModal()
      }
    }
  }

  onMoveUp = () =>
    this.setState(({ dropDownIndex }) => ({
      dropDownIndex: dropDownIndex - 1,
    }))

  onMoveDown = () =>
    this.setState(({ dropDownIndex }) => ({
      dropDownIndex: dropDownIndex === null ? 0 : dropDownIndex + 1,
    }))

  hideDropDown = () =>
    this.setState({ isDropDownOpen: false, dropDownIndex: null })
  showDropDown = () => this.setState({ isDropDownOpen: true })

  onFormSubmit = event => {
    event.preventDefault()
    event.stopPropagation()
    this.createChannel()
  }

  setName = event =>
    this.setState({
      channelName: event.target.value,
      isDropDownOpen: this.hasPopularNamesMatch(),
      dropDownIndex: null,
    })

  toggleChannelType = () => {
    this.setState(previousState => {
      const type =
        previousState.type === ChannelTypes.PUBLIC
          ? ChannelTypes.PRIVATE
          : ChannelTypes.PUBLIC
      const showMembershipPicker =
        type === ChannelTypes.PRIVATE
          ? true
          : previousState.showMembershipPicker
      return {
        type,
        showMembershipPicker,
      }
    })
  }

  updateUserRole = ({ user, role }) => {
    if (role === 'none') {
      this.setState(state => ({
        usersMemberships: state.usersMemberships.filter(
          membership => membership.user.id !== user.id
        ),
      }))
    } else {
      this.setState(state => {
        const membershipIndex = state.usersMemberships.reduce(
          (result, membership, index) => {
            if (membership.user.id === user.id) {
              return index
            }
            return result
          },
          -1
        )
        return {
          usersMemberships: replaceInArray(
            state.usersMemberships,
            { user, role },
            membershipIndex
          ),
        }
      })
    }
  }

  updateUserGroupRole = ({ userGroup, role }) => {
    if (role === 'none') {
      this.setState(state => ({
        userGroupsMemberships: state.userGroupsMemberships.filter(
          membership => membership.userGroup.id !== userGroup.id
        ),
      }))
    } else {
      this.setState(state => {
        const membershipIndex = state.userGroupsMemberships.reduce(
          (result, membership, index) => {
            if (membership.userGroup.id === userGroup.id) {
              return index
            }
            return result
          },
          -1
        )
        return {
          userGroupsMemberships: replaceInArray(
            state.userGroupsMemberships,
            { userGroup, role },
            membershipIndex
          ),
        }
      })
    }
  }

  addUserMembership = membership => {
    this.setState(state => ({
      usersMemberships: [...state.usersMemberships, membership],
    }))
  }

  addUserGroupMembership = membership => {
    this.setState(state => ({
      userGroupsMemberships: [...state.userGroupsMemberships, membership],
    }))
  }
}

function replaceInArray(arr, element, index) {
  return [...arr.slice(0, index), element, ...arr.slice(index + 1, arr.length)]
}
